import { images, type ImageName } from '../generated/images';

type Props = {
  name: ImageName;
  alt: string;
  /** The `sizes` attribute: how wide the image renders at each breakpoint. */
  sizes: string;
  className?: string;
  imgClassName?: string;
  /** Above-the-fold images: load eagerly with high fetch priority. */
  priority?: boolean;
};

const srcset = (name: string, widths: readonly number[], ext: 'avif' | 'jpg') =>
  widths.map((w) => `/images/${name}-${w}.${ext} ${w}w`).join(', ');

/**
 * Responsive image: AVIF via <source>, JPEG fallback in <img>, explicit width/height to
 * prevent layout shift. Images inside <picture> are not auto-preloaded by React 19's
 * server renderer, so below-the-fold images never compete with critical resources.
 */
export function Picture({ name, alt, sizes, className, imgClassName, priority = false }: Props) {
  const img = images[name];
  const largest = img.widths[img.widths.length - 1];
  return (
    <picture className={className}>
      <source type="image/avif" srcSet={srcset(name, img.widths, 'avif')} sizes={sizes} />
      <img
        src={`/images/${name}-${largest}.jpg`}
        srcSet={srcset(name, img.widths, 'jpg')}
        sizes={sizes}
        width={img.width}
        height={img.height}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        decoding={priority ? 'auto' : 'async'}
        fetchPriority={priority ? 'high' : undefined}
        className={imgClassName}
      />
    </picture>
  );
}
